/**
 * TWA Quiz Funnel
 * Handles multiple quiz instances on a single page
 * Saves answers to localStorage for Klaviyo integration
 */
(function() {
    'use strict';

    const STORAGE_KEY_PREFIX = 'twa_quiz_';

    class TWAQuizFunnel {
        constructor(container) {
            this.container = container;
            this.sectionId = container.dataset.sectionId;
            this.quizId = container.dataset.quizId || this.sectionId;
            this.storageKey = STORAGE_KEY_PREFIX + this.quizId;
            this.currentStep = 0;
            this.totalSteps = 0;
            this.answers = {};
            this.originalBodyBg = null;
            
            this.init();
        }

        init() {
            this.steps = this.container.querySelectorAll('.twa__quiz-step-content');
            this.totalSteps = this.steps.length;

            if (this.totalSteps === 0) return;

            this.buildProgressSteps();

            // Load saved answers from localStorage
            this.loadFromStorage();

            // Initialize step indexes and numbers
            this.initializeSteps();
            
            // Restore previous selections
            this.restoreSelections();
            this.restoreTarotSelections();

            this.validateCurrentStep();
            if (this.currentStep >= this.totalSteps) {
                this.currentStep = 0;
            }
            
            this.bindEvents();
            this.initReviewSteps();
            this.showStep(this.currentStep);
            this.updateProgress();
        }

        // localStorage methods
        loadFromStorage() {
            try {
                const stored = localStorage.getItem(this.storageKey);
                if (stored) {
                    const data = JSON.parse(stored);
                    if (data && typeof data === 'object') {
                        this.answers = data.answers || {};
                        this.currentStep = data.currentStep || 0;
                    }
                }
            } catch (e) {
                console.warn('TWA Quiz: Could not load from localStorage', e);
            }
        }

        saveToStorage() {
            try {
                const data = {
                    quizId: this.quizId,
                    answers: this.answers,
                    currentStep: this.currentStep,
                    completedAt: this.completedAt || null,
                    updatedAt: new Date().toISOString()
                };
                localStorage.setItem(this.storageKey, JSON.stringify(data));
                
                // Dispatch event for external listeners (e.g., Klaviyo)
                window.dispatchEvent(new CustomEvent('twa:quiz-updated', {
                    detail: data
                }));
            } catch (e) {
                console.warn('TWA Quiz: Could not save to localStorage', e);
            }
        }

        clearStorage() {
            try {
                localStorage.removeItem(this.storageKey);
            } catch (e) {
                console.warn('TWA Quiz: Could not clear localStorage', e);
            }
        }

        restoreSelections() {
            // Restore radio selections from saved answers
            Object.entries(this.answers).forEach(([name, value]) => {
                const input = this.container.querySelector(
                    `input[name="${name}"][value="${value}"]:not(.twa__tarot-card-input)`
                );
                if (input) {
                    input.checked = true;
                    this.markChoiceAsPreviouslySelected(input);
                    this.enableContinueForStep(input.closest('.twa__quiz-step-content'));
                }
            });
        }

        restoreTarotSelections() {
            Object.entries(this.answers).forEach(([name, value]) => {
                const input = this.container.querySelector(
                    `.twa__tarot-card-input[name="${name}"][value="${value}"]`
                );
                if (input) {
                    const card = input.closest('.twa__tarot-card');
                    if (card) {
                        this.selectTarotCard(card, false);
                        this.markChoiceAsPreviouslySelected(input);
                        this.enableContinueForStep(input.closest('.twa__quiz-step-content'));
                    }
                }
            });
        }

        isConditionalStep(stepContent) {
            return stepContent?.dataset.conditionalStep === 'true';
        }

        isInfoStep(stepContent) {
            if (!stepContent) {
                return false;
            }

            return (
                stepContent.dataset.infoStep === 'true'
                || stepContent.dataset.reviewStep === 'true'
                || this.isConditionalStep(stepContent)
            );
        }

        isProgressStep(stepContent) {
            if (!stepContent) {
                return false;
            }

            return (
                !stepContent.classList.contains('twa__quiz-step-content--final')
                && !this.isInfoStep(stepContent)
            );
        }

        buildProgressSteps() {
            const container = this.container.querySelector('.twa__quiz-funnel-steps');
            if (!container) {
                this.progressSteps = [];
                return;
            }

            const iconSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M0.75 5.55L3.60714 8.75L10.75 0.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

            container.innerHTML = '';

            this.steps.forEach((step) => {
                if (!this.isProgressStep(step)) {
                    return;
                }

                const progressStep = document.createElement('div');
                progressStep.className = 'twa__quiz-funnel-step';
                progressStep.innerHTML = `<div class="twa__quiz-funnel-icon">${iconSvg}</div>`;
                container.appendChild(progressStep);
            });

            this.progressSteps = container.querySelectorAll('.twa__quiz-funnel-step');
        }

        shouldShowConditionalStep(stepContent) {
            if (!this.isConditionalStep(stepContent)) {
                return false;
            }

            const inputName = stepContent.dataset.triggerInputName;
            const showWhen = stepContent.dataset.showWhenValue;

            if (!inputName || showWhen === undefined) {
                return false;
            }

            return this.answers[inputName] === showWhen;
        }

        getNextStepIndex(fromIndex) {
            let nextIndex = fromIndex + 1;

            while (nextIndex < this.totalSteps) {
                const step = this.steps[nextIndex];

                if (this.isConditionalStep(step)) {
                    if (this.shouldShowConditionalStep(step)) {
                        return nextIndex;
                    }

                    nextIndex += 1;
                    continue;
                }

                return nextIndex;
            }

            return nextIndex;
        }

        getPreviousStepIndex(fromIndex) {
            let prevIndex = fromIndex - 1;

            while (prevIndex >= 0) {
                const step = this.steps[prevIndex];

                if (this.isConditionalStep(step)) {
                    prevIndex -= 1;
                    continue;
                }

                return prevIndex;
            }

            return 0;
        }

        getLogicalProgressIndex(stepIndex) {
            let logical = 0;

            for (let i = 0; i < stepIndex; i += 1) {
                if (this.isProgressStep(this.steps[i])) {
                    logical += 1;
                }
            }

            return logical;
        }

        validateCurrentStep() {
            const step = this.steps[this.currentStep];

            if (
                step
                && this.isConditionalStep(step)
                && !this.shouldShowConditionalStep(step)
            ) {
                const fallback = this.getNextStepIndex(this.currentStep - 1);

                if (fallback < this.totalSteps) {
                    this.currentStep = fallback;
                }
            }
        }

        enableContinueForStep(stepContent) {
            if (!stepContent) return;

            const continueBtn = stepContent.querySelector('.twa__quiz-continue-btn');
            if (continueBtn) {
                continueBtn.classList.add('twa__btn-enabled');
            }
        }

        isMultiSelectStep(stepContent) {
            if (!stepContent) {
                return false;
            }

            const choicesWrapper = stepContent.querySelector(
                '.twa__quiz-choices-wrapper'
            );

            if (!choicesWrapper) {
                return false;
            }

            if (choicesWrapper.querySelector('[data-multi-select="true"]')) {
                return true;
            }

            if (choicesWrapper.querySelector('.twa__quiz-choices--multi-select')) {
                return true;
            }

            return Boolean(
                choicesWrapper.querySelector('input[type="checkbox"]')
            );
        }

        shouldAutoContinue(stepContent) {
            if (!stepContent || stepContent.dataset.autoContinue !== 'true') {
                return false;
            }

            if (stepContent.classList.contains('twa__quiz-step-content--final')
                || stepContent.classList.contains('twa__quiz-step-content--review')
                || stepContent.dataset.reviewStep === 'true'
                || stepContent.dataset.infoStep === 'true'
                || stepContent.dataset.conditionalStep === 'true'
            ) {
                return false;
            }

            if (this.isMultiSelectStep(stepContent)) {
                return false;
            }

            if (stepContent.querySelector('.twa__tarot-choice')) {
                return false;
            }

            return true;
        }

        applyAutoContinueUi(stepContent) {
            if (!stepContent) {
                return;
            }

            const continueBtn = stepContent.querySelector('.twa__quiz-continue-btn');
            if (!continueBtn) {
                return;
            }

            const autoContinue = this.shouldAutoContinue(stepContent);

            stepContent.classList.toggle(
                'twa__quiz-step--auto-continue',
                autoContinue
            );
            continueBtn.hidden = autoContinue;
        }

        advanceFromStep(stepContent) {
            if (!stepContent) {
                return;
            }

            const stepIndex = Number(stepContent.dataset.stepIndex);

            if (Number.isNaN(stepIndex)) {
                return;
            }

            if (stepIndex < this.totalSteps - 1) {
                this.goToStep(this.getNextStepIndex(stepIndex));
                return;
            }

            this.handleQuizComplete();
        }

        initializeSteps() {
            this.steps.forEach((step, index) => {
                // Set data attribute for step index
                step.dataset.stepIndex = index;
                
                // Set step number text
                const stepNumber = step.querySelector('.twa__quiz-step-number');
                if (stepNumber) {
                    stepNumber.textContent = index + 1;
                }
                
                // Show/hide back button based on step index
                const backBtn = step.querySelector('.twa__quiz-back-btn');
                const isFinalStep = step.classList.contains(
                    'twa__quiz-step-content--final'
                );
                if (backBtn) {
                    if (isFinalStep) {
                        backBtn.style.display = 'none';
                    } else {
                        backBtn.style.display = index > 0 ? 'flex' : 'none';
                    }
                }

                // Add class to first step's continue button
                if (index === 0) {
                    const continueBtn = step.querySelector('.twa__quiz-continue-btn');
                    if (continueBtn) {
                        continueBtn.classList.add('twa__btn-first');
                    }
                }

                if (step.dataset.reviewStep === 'true'
                    || step.dataset.infoStep === 'true'
                    || step.dataset.conditionalStep === 'true'
                ) {
                    this.enableContinueForStep(step);
                }

                this.applyAutoContinueUi(step);
            });
        }

        initReviewSteps() {
            this.container.querySelectorAll(
                '.twa__quiz-step-content--review'
            ).forEach((reviewStep) => {
                this.initReviewStep(reviewStep);
            });
        }

        initReviewStep(reviewStep) {
            if (reviewStep.dataset.reviewInitialized === 'true') {
                return;
            }

            const sliderEl = reviewStep.querySelector('.twa__quiz-review-slider');
            if (!sliderEl) {
                reviewStep.dataset.reviewInitialized = 'true';
                return;
            }
            const tabsContainer = reviewStep.querySelector('.twa__quiz-review-tabs');
            const slides = sliderEl.querySelectorAll('.swiper-slide[data-tab-title]');

            if (slides.length === 0) {
                reviewStep.dataset.reviewInitialized = 'true';
                return;
            }

            const buildTabs = () => {
                if (!tabsContainer) {
                    return;
                }

                const tabOrder = [];
                const tabLabels = {};

                slides.forEach((slide) => {
                    const tabKey = slide.dataset.tabTitle;

                    if (!tabKey || tabOrder.includes(tabKey)) {
                        return;
                    }

                    tabOrder.push(tabKey);
                    tabLabels[tabKey] = slide.dataset.tabLabel || tabKey;
                });

                tabsContainer.innerHTML = '';

                tabOrder.forEach((tabKey, index) => {
                    const tabButton = document.createElement('button');
                    tabButton.type = 'button';
                    tabButton.className = 'twa__quiz-review-tab';
                    tabButton.setAttribute('role', 'tab');
                    tabButton.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
                    tabButton.setAttribute('aria-controls', sliderEl.id);
                    tabButton.dataset.tabTitle = tabKey;
                    tabButton.textContent = tabLabels[tabKey];

                    if (index === 0) {
                        tabButton.classList.add('twa__quiz-review-tab--active');
                    }

                    tabsContainer.appendChild(tabButton);
                });
            };

            buildTabs();

            const getActiveTab = () => {
                const activeTab = reviewStep.querySelector(
                    '.twa__quiz-review-tab--active'
                );
                return activeTab?.dataset.tabTitle || '';
            };

            const filterSlides = (tabTitle) => {
                slides.forEach((slide) => {
                    const matches = slide.dataset.tabTitle === tabTitle;
                    slide.classList.toggle('twa__quiz-review-slide--hidden', !matches);
                    slide.setAttribute('aria-hidden', matches ? 'false' : 'true');
                });
            };

            const refreshSwiper = () => {
                if (!swiperInstance) {
                    return;
                }

                swiperInstance.slideTo(0, 0);
                swiperInstance.update();

                requestAnimationFrame(() => {
                    if (swiperInstance) {
                        swiperInstance.update();
                    }
                });
            };

            let swiperInstance = null;

            const initSwiper = () => {
                if (typeof Swiper === 'undefined') {
                    return;
                }

                if (swiperInstance) {
                    swiperInstance.destroy(true, true);
                    swiperInstance = null;
                }

                swiperInstance = new Swiper(sliderEl, {
                    slidesPerView: 1.8,
                    spaceBetween: 6,
                    watchOverflow: true,
                    observer: true,
                    observeParents: true,
                    breakpoints: {
                        750: {
                            slidesPerView: 4,
                            spaceBetween: 12,
                        },
                    },
                });

                reviewStep.reviewSwiper = swiperInstance;
            };

            const initialTab = getActiveTab() || slides[0]?.dataset.tabTitle || '';
            filterSlides(initialTab);
            initSwiper();

            if (reviewStep.dataset.reviewTabsBound !== 'true') {
                tabsContainer?.addEventListener('click', (event) => {
                    const tab = event.target.closest('.twa__quiz-review-tab');

                    if (!tab || tab.classList.contains('twa__quiz-review-tab--active')) {
                        return;
                    }

                    const tabTitle = tab.dataset.tabTitle;
                    if (!tabTitle) {
                        return;
                    }

                    reviewStep.querySelectorAll('.twa__quiz-review-tab').forEach((otherTab) => {
                        const isActive = otherTab === tab;
                        otherTab.classList.toggle(
                            'twa__quiz-review-tab--active',
                            isActive
                        );
                        otherTab.setAttribute('aria-selected', isActive ? 'true' : 'false');
                    });

                    filterSlides(tabTitle);
                    refreshSwiper();
                });

                reviewStep.addEventListener('click', (event) => {
                    const btn = event.target.closest('.twa__quiz-review-play-btn');
                    if (!btn) {
                        return;
                    }

                    const slide = btn.closest('.twa__quiz-review-slide');
                    const video = slide?.querySelector('.twa__quiz-review-slide-video');

                    if (!video) {
                        return;
                    }

                    if (video.paused) {
                        video.muted = false;
                        video.play().catch(() => {});
                        btn.setAttribute('aria-label', 'Pause video review');
                    } else {
                        video.pause();
                        btn.setAttribute('aria-label', 'Play video review');
                    }
                });

                reviewStep.dataset.reviewTabsBound = 'true';
            }

            reviewStep.dataset.reviewInitialized = 'true';
        }

        updateReviewStep(reviewStep) {
            if (!reviewStep || reviewStep.dataset.reviewInitialized !== 'true') {
                return;
            }

            if (reviewStep.reviewSwiper) {
                requestAnimationFrame(() => {
                    reviewStep.reviewSwiper.update();
                });
            }
        }

        bindEvents() {
            const choiceSelector = '.twa__quiz-choice-text, .twa__quiz-choice-image, .twa__quiz-choice-large-image';

            this.container.querySelectorAll(
                'input[type="radio"]:not(.twa__tarot-card-input)'
            ).forEach((input) => {
                input.addEventListener('change', (e) => this.handleChoiceSelection(e));
            });

            this.container.addEventListener('mousedown', (e) => {
                const choice = e.target.closest(choiceSelector);
                if (!choice) {
                    return;
                }

                const input = choice.querySelector('input[type="radio"]');
                if (input) {
                    input.dataset.twaWasCheckedOnPointer = input.checked ? '1' : '0';
                }
            });

            this.container.addEventListener('click', (e) => {
                const choice = e.target.closest(choiceSelector);
                if (!choice) {
                    return;
                }

                const input = choice.querySelector('input[type="radio"]');
                if (!input) {
                    return;
                }

                const stepContent = input.closest('.twa__quiz-step-content');
                if (!stepContent || !this.shouldAutoContinue(stepContent)) {
                    return;
                }

                const wasAlreadyChecked = input.dataset.twaWasCheckedOnPointer === '1';

                requestAnimationFrame(() => {
                    if (!input.checked) {
                        return;
                    }

                    if (wasAlreadyChecked) {
                        this.processChoiceSelection(input, stepContent);
                    } else {
                        this.markChoiceAsPreviouslySelected(input);
                    }
                });
            });

            // Continue button events
            this.container.querySelectorAll('.twa__quiz-continue-btn').forEach(btn => {
                btn.addEventListener('click', (e) => this.handleContinue(e));
            });

            // Back button events
            this.container.querySelectorAll('.twa__quiz-back-btn').forEach(btn => {
                btn.addEventListener('click', (e) => this.handleBack(e));
            });

            this.bindTarotEvents();
            this.bindFinalCtaEvents();
        }

        bindFinalCtaEvents() {
            this.container.querySelectorAll(
                '.twa__quiz-final-cta[data-redirect-url]'
            ).forEach((cta) => {
                cta.addEventListener('click', (e) => this.handleFinalCtaClick(e));
            });
        }

        isTarotStep(stepContent) {
            return Boolean(stepContent?.querySelector('.twa__tarot-choice'));
        }

        bindTarotEvents() {
            this.container.querySelectorAll('.twa__tarot-choice').forEach((tarotGroup) => {
                const cards = tarotGroup.querySelectorAll('.twa__tarot-card');

                cards.forEach((card) => {
                    const btn = card.querySelector('.twa__tarot-card-btn');
                    const input = card.querySelector('.twa__tarot-card-input');

                    if (!btn || !input) return;

                    btn.addEventListener('click', () => {
                        const stepContent = card.closest('.twa__quiz-step-content');
                        if (!stepContent) {
                            return;
                        }

                        if (card.classList.contains('is-flipped')) {
                            input.checked = true;
                            this.processChoiceSelection(input, stepContent);
                            return;
                        }

                        this.selectTarotCard(card, true);
                        input.checked = true;
                        this.processChoiceSelection(input, stepContent);
                    });
                });
            });
        }

        selectTarotCard(card, animateReading) {
            const tarotGroup = card.closest('.twa__tarot-choice');
            if (!tarotGroup) return;

            const readingArea = tarotGroup.querySelector('.twa__tarot-reading');
            const cards = tarotGroup.querySelectorAll('.twa__tarot-card');
            const panel = card.querySelector('.twa__tarot-reading-panel');

            cards.forEach((otherCard) => {
                otherCard.classList.remove('is-flipped', 'is-selected');
                const otherBtn = otherCard.querySelector('.twa__tarot-card-btn');
                if (otherBtn) {
                    otherBtn.setAttribute('aria-pressed', 'false');
                }
            });

            card.classList.add('is-flipped', 'is-selected');
            const btn = card.querySelector('.twa__tarot-card-btn');
            if (btn) {
                btn.setAttribute('aria-pressed', 'true');
            }

            if (!readingArea || !panel) return;

            const title = panel.querySelector('.twa__tarot-reading-title');
            const content = panel.querySelector('.twa__tarot-reading-content');
            const titleEl = readingArea.querySelector('.twa__tarot-reading-title');
            const contentEl = readingArea.querySelector('.twa__tarot-reading-content');

            if (titleEl) {
                titleEl.textContent = title ? title.textContent : '';
                titleEl.hidden = !title;
            }

            if (contentEl) {
                contentEl.innerHTML = content ? content.innerHTML : '';
                contentEl.hidden = !content;
            }

            readingArea.hidden = false;
            readingArea.classList.remove('twa__tarot-reading--visible');

            if (animateReading) {
                requestAnimationFrame(() => {
                    readingArea.classList.add('twa__tarot-reading--visible');
                });
            } else {
                readingArea.classList.add('twa__tarot-reading--visible');
            }
        }

        resetTarotInContainer() {
            this.container.querySelectorAll('.twa__tarot-choice').forEach((tarotGroup) => {
                tarotGroup.querySelectorAll('.twa__tarot-card').forEach((card) => {
                    card.classList.remove('is-flipped', 'is-selected');
                    const btn = card.querySelector('.twa__tarot-card-btn');
                    if (btn) {
                        btn.setAttribute('aria-pressed', 'false');
                    }
                });

                const readingArea = tarotGroup.querySelector('.twa__tarot-reading');
                if (readingArea) {
                    readingArea.hidden = true;
                    readingArea.classList.remove('twa__tarot-reading--visible');
                    const titleEl = readingArea.querySelector('.twa__tarot-reading-title');
                    const contentEl = readingArea.querySelector('.twa__tarot-reading-content');
                    if (titleEl) titleEl.textContent = '';
                    if (contentEl) contentEl.innerHTML = '';
                }
            });
        }

        handleChoiceSelection(e) {
            const input = e.target;
            const stepContent = input.closest('.twa__quiz-step-content');
            this.processChoiceSelection(input, stepContent);
        }

        markChoiceAsPreviouslySelected(input) {
            if (input?.checked) {
                input.dataset.twaPriorChecked = '1';
            }
        }

        processChoiceSelection(input, stepContent) {
            if (!input || !stepContent) {
                return;
            }

            const inputName = input.name;
            const value = input.value;

            this.answers[inputName] = value;
            this.saveToStorage();
            this.enableContinueForStep(stepContent);
            this.markChoiceAsPreviouslySelected(input);

            if (this.shouldAutoContinue(stepContent)) {
                this.advanceFromStep(stepContent);
            }
        }

        handleContinue(e) {
            e.preventDefault();
            const btn = e.target.closest('.twa__quiz-continue-btn');
            
            if (!btn.classList.contains('twa__btn-enabled')) return;

            const nextStep = this.getNextStepIndex(this.currentStep);

            if (nextStep < this.totalSteps) {
                this.goToStep(nextStep);
            } else {
                // Last step - trigger completion
                this.handleQuizComplete();
            }
        }

        handleBack(e) {
            e.preventDefault();
            if (this.currentStep > 0) {
                this.goToStep(this.getPreviousStepIndex(this.currentStep));
            }
        }

        goToStep(stepIndex) {
            if (stepIndex < 0 || stepIndex >= this.totalSteps) return;

            const previousStep = this.currentStep;
            this.currentStep = stepIndex;

            // Show next step before hiding the previous one to avoid layout flicker
            this.showStep(this.currentStep);
            if (previousStep !== stepIndex) {
                this.hideStep(previousStep);
            }

            this.updateProgress();

            // Save current step to localStorage
            this.saveToStorage();

            this.scrollQuizIntoView();
        }

        scrollQuizIntoView() {
            const rect = this.container.getBoundingClientRect();
            const offset = parseInt(
                getComputedStyle(this.container)
                    .getPropertyValue('--twa-quiz-viewport-offset'),
                10
            ) || 0;

            if (rect.top >= 0 && rect.top <= offset) {
                return;
            }

            this.container.scrollIntoView({
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)')
                    .matches
                    ? 'auto'
                    : 'smooth',
                block: 'start',
            });
        }

        showStep(index) {
            const step = this.steps[index];
            if (step) {
                step.classList.add('twa__step-visible');
                
                // Check if this step already has a selection
                const selectedInput = step.querySelector('input[type="radio"]:checked');
                const continueBtn = step.querySelector('.twa__quiz-continue-btn');
                
                if (selectedInput && continueBtn) {
                    continueBtn.classList.add('twa__btn-enabled');
                    this.markChoiceAsPreviouslySelected(selectedInput);
                }

                if (this.isTarotStep(step)) {
                    const selectedTarotInput = step.querySelector(
                        '.twa__tarot-card-input:checked'
                    );

                    if (selectedTarotInput) {
                        this.enableContinueForStep(step);
                        this.markChoiceAsPreviouslySelected(selectedTarotInput);
                    }
                }

                if (step.dataset.reviewStep === 'true') {
                    this.enableContinueForStep(step);
                    this.updateReviewStep(step);
                }

                if (
                    step.dataset.infoStep === 'true'
                    || step.dataset.conditionalStep === 'true'
                ) {
                    this.enableContinueForStep(step);
                }

                this.applyAutoContinueUi(step);

                this.updatePageBackground(step);
                this.setFinalStepMode(step);
            }
        }

        hideStep(index) {
            const step = this.steps[index];
            if (step) {
                step.classList.remove('twa__step-visible');
                this.clearPageBackground(step);

                if (step.classList.contains('twa__quiz-step-content--final')) {
                    this.container.classList.remove('twa__quiz-funnel--final-step');
                }

                if (step.dataset.reviewStep === 'true') {
                    this.container.classList.remove('twa__quiz-funnel--review-step');
                }
            }
        }

        setFinalStepMode(step) {
            const isFinal = step?.classList.contains(
                'twa__quiz-step-content--final'
            );
            const isReview = step?.dataset.reviewStep === 'true';

            this.container.classList.toggle(
                'twa__quiz-funnel--final-step',
                isFinal
            );
            this.container.classList.toggle(
                'twa__quiz-funnel--review-step',
                isReview
            );
        }

        updatePageBackground(step) {
            const pageBg = step?.dataset.pageBg;
            if (!pageBg) return;

            if (this.originalBodyBg === null) {
                this.originalBodyBg = document.body.style.backgroundColor;
            }

            document.body.style.backgroundColor = pageBg;
            document.body.classList.add('twa__quiz-final-page-bg');
        }

        clearPageBackground(step) {
            if (!step?.dataset.pageBg) return;

            document.body.style.backgroundColor = this.originalBodyBg || '';
            document.body.classList.remove('twa__quiz-final-page-bg');
        }

        updateProgress() {
            const logicalIndex = this.getLogicalProgressIndex(this.currentStep);

            this.progressSteps.forEach((step, index) => {
                step.classList.remove('twa__step-active', 'twa__step-completed');
                
                if (index < logicalIndex) {
                    step.classList.add('twa__step-completed');
                } else if (index === logicalIndex) {
                    step.classList.add('twa__step-active');
                }
            });
        }

        handleFinalCtaClick(e) {
            e.preventDefault();

            const cta = e.currentTarget;
            const redirectUrl = cta.dataset.redirectUrl
                || this.container.dataset.redirectUrl;
            const enabledStraightLink = cta.dataset.enabledStraightLink === 'true';
            if (enabledStraightLink) {
                window.location.href = redirectUrl;
                return;
            }

            if (!redirectUrl) {
                return;
            }

            if (cta.classList.contains('twa__quiz-final-cta--loading')) {
                return;
            }

            cta.classList.remove('twa__quiz-final-cta--error');
            this.setFinalCtaLoading(cta, true);

            this.completedAt = new Date().toISOString();
            this.saveToStorage();
            this.dispatchQuizCompleteEvent();

            const klaviyoEnabled = this.container.dataset.klaviyoEnabled !== 'false';
            const eventName = this.container.dataset.klaviyoEventName
                || 'Quiz Completed';

            const redirect = () => {
                window.location.href = buildRedirectUrl(
                    redirectUrl,
                    this.answers
                );
            };

            if (!klaviyoEnabled) {
                redirect();
                return;
            }

            sendQuizToKlaviyo({
                container: this.container,
                quiz: this,
                eventName: eventName,
            }).then(redirect).catch((err) => {
                console.warn('TWA Quiz: Klaviyo send failed', err);
                this.setFinalCtaLoading(cta, false);
                cta.classList.add('twa__quiz-final-cta--error');
            });
        }

        setFinalCtaLoading(cta, isLoading) {
            const label = cta.querySelector('.twa__quiz-final-cta-label');
            const loadingEl = cta.querySelector('.twa__quiz-final-cta-loading');

            cta.classList.toggle('twa__quiz-final-cta--loading', isLoading);
            cta.setAttribute('aria-busy', isLoading ? 'true' : 'false');

            if (label) {
                label.hidden = isLoading;
            }

            if (loadingEl) {
                loadingEl.hidden = !isLoading;
            }
        }

        dispatchQuizCompleteEvent() {
            const event = new CustomEvent('twa:quiz-complete', {
                detail: {
                    quizId: this.quizId,
                    sectionId: this.sectionId,
                    answers: this.answers,
                    completedAt: this.completedAt,
                },
                bubbles: true,
            });
            this.container.dispatchEvent(event);
        }

        handleQuizComplete() {
            this.completedAt = new Date().toISOString();
            this.saveToStorage();
            this.dispatchQuizCompleteEvent();

            const redirectUrl = this.container.dataset.redirectUrl;
            if (redirectUrl) {
                window.location.href = buildRedirectUrl(
                    redirectUrl,
                    this.answers
                );
            }
        }

        // Public methods
        getAnswers() {
            return this.answers;
        }

        getCurrentStep() {
            return this.currentStep;
        }

        getStoredData() {
            try {
                const stored = localStorage.getItem(this.storageKey);
                return stored ? JSON.parse(stored) : null;
            } catch (e) {
                return null;
            }
        }

        reset(clearStorage = true) {
            this.answers = {};
            this.currentStep = 0;
            this.completedAt = null;
            
            if (clearStorage) {
                this.clearStorage();
            }
            
            // Reset all radio buttons
            this.container.querySelectorAll('input[type="radio"]').forEach(input => {
                input.checked = false;
            });

            this.resetTarotInContainer();

            // Disable all continue buttons
            this.container.querySelectorAll('.twa__quiz-continue-btn').forEach(btn => {
                btn.classList.remove('twa__btn-enabled');
            });

            // Hide all steps and show first
            this.steps.forEach(step => step.classList.remove('twa__step-visible'));
            this.showStep(0);
            this.updateProgress();
        }
    }

    // Initialize all quiz funnels on page
    function initQuizFunnels() {
        const quizContainers = document.querySelectorAll('.twa__quiz-funnel-container[data-section-id]');
        
        quizContainers.forEach(container => {
            // Check if already initialized
            if (container.dataset.initialized === 'true') return;
            
            const quiz = new TWAQuizFunnel(container);
            container.dataset.initialized = 'true';
            
            // Store instance on element for external access
            container.quizInstance = quiz;
        });
    }

    // Helper function to get all quiz answers from localStorage (for Klaviyo)
    function getAllQuizAnswers() {
        const answers = {};
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
                    const data = JSON.parse(localStorage.getItem(key));
                    answers[data.quizId] = data;
                }
            }
        } catch (e) {
            console.warn('TWA Quiz: Could not get all quiz answers', e);
        }
        return answers;
    }

    /**
     * Build redirect URL with quiz answers as query parameters.
     * @param {string} redirectUrl
     * @param {Object} answers
     * @returns {string}
     */
    function buildRedirectUrl(redirectUrl, answers) {
        const url = new URL(redirectUrl, window.location.origin);

        Object.entries(answers).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                url.searchParams.append(key, value);
            }
        });

        url.searchParams.set('quiz_completed', '1');

        return url.toString();
    }

    /**
     * Resolve email for Klaviyo identify from container, URL, or final step input.
     * @param {HTMLElement} container
     * @returns {string|null}
     */
    function getEmailForKlaviyo(container) {
        const customerEmail = container.dataset.customerEmail;
        if (customerEmail) {
            return customerEmail;
        }

        const urlEmail = new URLSearchParams(window.location.search).get('email');
        if (urlEmail) {
            return urlEmail;
        }

        const finalStep = container.querySelector(
            '.twa__quiz-step-content--final'
        );
        const emailInput = finalStep?.querySelector(
            '[data-quiz-email], input[type="email"]'
        );

        if (emailInput?.value) {
            return emailInput.value.trim();
        }

        return null;
    }

    /**
     * Flatten quiz answers for a single quiz into Klaviyo properties.
     * @param {Object} quizInstance
     * @returns {Object}
     */
    function getKlaviyoPropertiesForQuiz(quizInstance) {
        const properties = {
            quiz_id: quizInstance.quizId,
        };

        Object.entries(quizInstance.answers || {}).forEach(([answerKey, value]) => {
            properties[`quiz_${answerKey}`] = value;
        });

        if (quizInstance.completedAt) {
            properties[`quiz_${quizInstance.quizId}_completed`] = true;
            properties[`quiz_${quizInstance.quizId}_completed_at`] = quizInstance.completedAt;
        }

        return properties;
    }

    /**
     * @param {Promise|undefined} maybePromise
     * @param {number} ms
     * @returns {Promise}
     */
    function resolveWithTimeout(maybePromise, ms) {
        if (!maybePromise || typeof maybePromise.then !== 'function') {
            return new Promise((resolve) => {
                setTimeout(resolve, ms);
            });
        }

        return Promise.race([
            maybePromise,
            new Promise((resolve) => {
                setTimeout(resolve, ms);
            }),
        ]);
    }

    /**
     * Send quiz answers to Klaviyo via onsite identify + track.
     * @param {Object} options
     * @param {HTMLElement} options.container
     * @param {TWAQuizFunnel} options.quiz
     * @param {string} options.eventName
     * @returns {Promise<void>}
     */
    function sendQuizToKlaviyo({ container, quiz, eventName }) {
        const properties = getKlaviyoPropertiesForQuiz(quiz);
        const email = getEmailForKlaviyo(container);

        const runLegacyQueue = () => {
            window._learnq = window._learnq || [];

            if (email) {
                window._learnq.push(['identify', {
                    $email: email,
                    ...properties,
                }]);
            }

            window._learnq.push(['track', eventName, properties]);

            return resolveWithTimeout(undefined, 400);
        };

        if (!window.klaviyo) {
            return runLegacyQueue();
        }

        const runTrack = () => {
            try {
                const trackResult = window.klaviyo.track(eventName, properties);
                return resolveWithTimeout(trackResult, 3000);
            } catch (err) {
                return Promise.reject(err);
            }
        };

        if (!email) {
            return runTrack();
        }

        try {
            const identifyPayload = {
                $email: email,
                ...properties,
            };
            const identifyResult = window.klaviyo.identify(identifyPayload);

            return resolveWithTimeout(identifyResult, 3000).then(runTrack);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    // Helper function to get flattened answers for Klaviyo custom properties
    function getKlaviyoProperties() {
        const properties = {};

        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);

                if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
                    const data = JSON.parse(localStorage.getItem(key));

                    Object.entries(data.answers || {}).forEach(([answerKey, value]) => {
                        properties[`quiz_${answerKey}`] = value;
                    });

                    if (data.completedAt) {
                        properties[`quiz_${data.quizId}_completed`] = true;
                        properties[`quiz_${data.quizId}_completed_at`] = data.completedAt;
                    }
                }
            }
        } catch (e) {
            console.warn('TWA Quiz: Could not get Klaviyo properties', e);
        }

        return properties;
    }

    // Clear all quiz data from localStorage
    function clearAllQuizData() {
        try {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
        } catch (e) {
            console.warn('TWA Quiz: Could not clear all quiz data', e);
        }
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initQuizFunnels);
    } else {
        initQuizFunnels();
    }

    // Re-initialize on Shopify section load (for theme editor)
    document.addEventListener('shopify:section:load', function(e) {
        if (e.target.querySelector('.twa__quiz-funnel-container')) {
            initQuizFunnels();
        }
    });

    // Expose to global scope for external access
    window.TWAQuizFunnel = TWAQuizFunnel;
    window.initTWAQuizFunnels = initQuizFunnels;
    window.TWAQuizHelpers = {
        getAllQuizAnswers,
        getKlaviyoProperties,
        getKlaviyoPropertiesForQuiz,
        sendQuizToKlaviyo,
        buildRedirectUrl,
        clearAllQuizData,
        STORAGE_KEY_PREFIX,
    };

})();
